import React, { useState } from "react";
import { Divider, MenuItem, useTheme } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { postNotification } from "../../../../components/utilities/Notifications";
import StyledMenu from "../../../../components/layout/StyledMenu";
import ExportModal from "../../../../components/modals/ExportModal";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { tabIndexes } from "../../EventOrganize";
import {
  deleteData,
  isUsingWS,
  postData
} from "../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { refreshPairings } from "../EventFunctions";
import PrintPairingsModal from "../../../../components/modals/PrintPairingsModal";
import { BetaPill } from "../../../../components/utilities/BetaFeaturePopup";

export function OptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    event,
    roundNo,
    setEvent,
    setRoundTimerOptions,
    setTopCutOptions,
    setCreatePairingModal,
    setRefreshPairings,
    setLoading,
    authContext,
    handleTabChange,
    setResetModalOpen
  } = props;
  const confirm = useConfirm();
  const open = Boolean(anchorEl);
  const [rosterModal, setRosterModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {event?.leagueEvent && (
          <>
            <MenuItem
              onClick={(e) => {
                setCreatePairingModal(true);
                setAnchorEl(null);
              }}
              disableRipple
            >
              Create Pairing
            </MenuItem>

            <Divider
              sx={{
                mt: 2,
                mb: 2
              }}
            />
          </>
        )}

        <MenuItem
          onClick={(e) => {
            setRoundTimerOptions(event);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Set up Round Timer
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setLoading(true);

            try {
              await postData(`/events/${event.id}/sendPairingsNotification`, {
                body: {
                  email: authContext?.authState?.identity?.email
                }
              });

              postNotification(
                "success",
                "Pairings notifications have been sent"
              );
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Send Pairings Notification
        </MenuItem>
        {!event?.topCut &&
          !event?.singleElimination &&
          !event?.doubleElimination && (
            <MenuItem
              onClick={(e) => {
                setTopCutOptions(event);
                setAnchorEl(null);
              }}
              disableRipple
            >
              Top Cut
            </MenuItem>
          )}
        <MenuItem
          onClick={async (e) => {
            setLoading(true);

            try {
              await deleteData(`/events/${event.id}/clearCache`);
              setRefreshPairings(true);
              postNotification("success", "Event cache cleared");
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Clear Event Cache
        </MenuItem>

        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);

            try {
              await confirm({
                title: `Confirm Defaults.`,
                description:
                  "Are you sure you wish to set all unsubmitted round by round soft scores to their default values?",
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }

            setLoading(true);
            try {
              await postData(`/events/${event.id}/processDefaultSoftScores`);
              setRefreshPairings(true);
              postNotification(
                "success",
                "Unsubmitted soft scores now set to defaults"
              );
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
          }}
          disableRipple
        >
          Set All Unsubmitted Soft Scores to Defaults
        </MenuItem>

        <MenuItem
          onClick={() => {
            refreshPairings();
          }}
        >
          Refresh Pairings
        </MenuItem>

        {event.requirePairingPublish && roundNo === event.currentRound && (
          <MenuItem
            onClick={async () => {
              try {
                await confirm({
                  title: "Are you sure?",
                  description:
                    "Are you sure you want to unpublish the current round's pairings?",
                  dialogProps: {
                    sx: {
                      zIndex: 99999
                    }
                  },
                  confirmationText: "Yes",
                  cancellationText: "No"
                });
              } catch (err) {
                return;
              }
              setLoading(true);
              try {
                await postData(`/events/${event.id}/unPublishPairings`, {
                  body: {
                    round: roundNo
                  }
                });
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
            }}
          >
            Unpublish Pairings
          </MenuItem>
        )}

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          onClick={(e) => {
            setPrintModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Printer Friendly Pairings <BetaPill size="small" sx={{ ml: 1 }} />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setRosterModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          Export Pairings
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            if (
              authContext?.authState?.identity?.isAdmin !== true &&
              !event?.userRole?.permissions?.circuitSubmission &&
              !authContext?.authState?.identity?.adminGameSystems?.includes(
                event?.gameSystemId
              )
            ) {
              postNotification(
                "error",
                "Your event user role does not have access to end a tournament"
              );
              return;
            }

            try {
              await confirm({
                title: `Confirm End Tournament.`,
                description: "Are you sure you want to End Tournament?",
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }
            setLoading(true);
            try {
              var updatedEvent = await postData(`/events/${event.id}`, {
                body: {
                  set: {
                    ended: true
                  }
                }
              });
              setEvent({ ...event, ...updatedEvent });
              handleTabChange(null, tabIndexes.placings, updatedEvent);
            } catch (err) {
              postNotification("error", err);
            }
            setLoading(false);
          }}
          disableRipple
        >
          End Tournament
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />
        <MenuItem
          onClick={async (e) => {
            setResetModalOpen(true);
            setAnchorEl(null);
          }}
          disableRipple
          sx={{ color: "error.main" }}
        >
          Reset To
        </MenuItem>
      </StyledMenu>
      <ExportModal
        label="Export Pairings"
        exportTypes={["pairings"]}
        event={event}
        setLoading={setLoading}
        open={rosterModal}
        setOpen={setRosterModal}
        authContext={authContext}
      />
      {event && roundNo && (
        <PrintPairingsModal
          label="Print Pairings"
          event={event}
          round={roundNo}
          open={printModal}
          setOpen={setPrintModal}
        />
      )}
    </>
  );
}

export function TeamPairingOptionsMenu(props) {
  const { anchorEl, setAnchorEl, setSwapTeamModal } = props;
  const open = Boolean(anchorEl);
  return (
    <StyledMenu
      id="pairing-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      <MenuItem
        onClick={(e) => {
          setSwapTeamModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        Swap Team
      </MenuItem>
    </StyledMenu>
  );
}

export function SeatedPairingOptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    setSwapSeatedPairingModal,
    setSwapSeatNumberModal
  } = props;
  const open = Boolean(anchorEl);
  return (
    <StyledMenu
      id="pairing-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      <MenuItem
        onClick={(e) => {
          setSwapSeatedPairingModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        Move to Different Table
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          setSwapSeatNumberModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        Change Seat Number
      </MenuItem>
    </StyledMenu>
  );
}

export function PairingOptionsMenu(props) {
  const {
    event,
    player,
    anchorEl,
    setAnchorEl,
    setArmyModalOpen,
    setSwapPlayerModal,
    setRefreshPairings
  } = props;
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const confirm = useConfirm();
  const [processing, setProcessing] = useState(false);
  return (
    <StyledMenu
      id="pairing-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {player?.id && (
        <>
          {player && getEnvironmentVariable("COMPANY_FLAG") !== "solforge" && (
            <MenuItem
              onClick={(e) => {
                setArmyModalOpen(true);
                setAnchorEl(null);
              }}
              disableRipple
            >
              {player?.armyListObjectId
                ? `Edit ${event?.gameSystem?.listLabel || "List"}`
                : `Add ${event?.gameSystem?.listLabel || "List"}`}
            </MenuItem>
          )}

          {player?.armyListObjectId && (
            <MenuItem
              href={
                player?.armyListSolforge?.id
                  ? `${
                      getEnvironmentVariable("HEADER_SOLFORGE_URL") || ""
                    }/fused/${player.armyListSolforge.id}`
                  : `/list/${player.armyListObjectId}`
              }
              target="_blank"
              component="a"
              disableRipple
            >
              View {event?.gameSystem?.listLabel || "List"}
            </MenuItem>
          )}
        </>
      )}
      <MenuItem
        onClick={(e) => {
          setSwapPlayerModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        Swap Player
      </MenuItem>
      {player?.id && !player?.dropped && (
        <MenuItem
          sx={{
            color: theme?.palette?.error.main
          }}
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: `Confirm Drop.`,
                description: "Are you sure you want to drop this player?",
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }

            setProcessing(true);
            try {
              await postData(`/players/${player?.id}`, {
                body: {
                  set: {
                    dropped: true
                  }
                }
              });
              setRefreshPairings(true);
            } catch (err) {
              postNotification("error", err);
            }
            setProcessing(false);
          }}
          disableRipple
        >
          Drop Player
        </MenuItem>
      )}
      {processing && <ModalLoadingFull />}
    </StyledMenu>
  );
}

export function PairingPopUpOptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    setLoading,
    setRefreshPairings,
    pairing,
    setPairing,
    event,
    authContext,
    setSwapTableModal
  } = props;

  const open = Boolean(anchorEl);
  const confirm = useConfirm();

  const useWebSockets = isUsingWS(authContext);

  return (
    <StyledMenu
      id="pairingpopup-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {pairing?.pairingType?.toLowerCase() === "pairing" &&
        Boolean(
          event.scorecardId &&
            event.gameSystem?.scorecardId &&
            event.scorecardId !== event.gameSystem.scorecardId
        ) && (
          <MenuItem
            component="a"
            href={`/game/${
              pairing?.pairingType
                ? pairing.pairingType.toLowerCase()
                : "pairing"
            }/${pairing?.id}?useGSD=true`}
            disableRipple
          >
            {getEnvironmentVariable("LABEL_GAMESYSTEM")} Scorecard
          </MenuItem>
        )}
      <MenuItem
        onClick={(e) => {
          setSwapTableModal(true);
          setAnchorEl(null);
        }}
        disableRipple
      >
        Change Table
      </MenuItem>
      <MenuItem
        onClick={async (e) => {
          try {
            await confirm({
              title: `Confirm Clear Scores.`,
              description:
                "Are you sure you want to clear the scores for this pairing?",
              dialogProps: {
                sx: {
                  zIndex: 99999
                }
              }
            });
          } catch (err) {
            return;
          }
          try {
            setLoading(true);
            await postData(`/pairings/${pairing.id}/clearScores`, {
              body: {
                pairingType: pairing?.pairingType || "Pairing"
              }
            });
            setRefreshPairings(true);
            postNotification("success", "Scores cleared");
            setLoading(false);
            setPairing(null);
            setAnchorEl(null);
          } catch (err) {
            postNotification("error", err);
            setLoading(false);
          }
        }}
        disableRipple
      >
        Clear Scores
      </MenuItem>

      <MenuItem
        onClick={async (e) => {
          setLoading(true);

          var metricDict = {
            gameResult: "gameResult",
            gamePoints: "gamePoints"
          };
          var body = {
            isTO: true,
            pairingType: pairing?.pairingType || "Pairing",
            isDone: true,
            metaData: {},
            gameData: {
              metrics: []
            }
          };
          if (authContext?.authState?.identity?.userId) {
            body.userId = authContext?.authState?.identity?.userId;
          }
          if (pairing?.metaData) {
            Object.keys(pairing.metaData).forEach((key) => {
              if (
                pairing.metaData[key] !== "" &&
                (typeof pairing.metaData[key] === "number" ||
                  !isNaN(Number(pairing.metaData[key])))
              ) {
                body.metaData[key] = "0";
              } else {
                body.metaData[key] = "";
              }
            });
          }

          let playerNo = 1;
          if (pairing.seatedPlayers?.length) {
            for (let seatedPlayer of pairing.seatedPlayers) {
              let player = `player${playerNo}`;
              body[`player${playerNo}Id`] = seatedPlayer.playerId;
              body.gameData[player] = {
                gameResult: 0,
                gamePoints: 0
              };
              if (pairing.games?.[seatedPlayer.playerId]) {
                for (let key of Object.keys(
                  pairing.games[seatedPlayer.playerId]
                )) {
                  metricDict[key] = key;
                  body.gameData[player][key] = 0;
                }
              }
              playerNo++;
            }
          } else {
            while (
              pairing &&
              pairing[
                `${body.pairingType === "TeamPairing" ? "teamPlayer" : "player"}${playerNo}`
              ]
            ) {
              let player = `player${playerNo}`;
              body.gameData[player] = {
                gameResult: 0,
                gamePoints: 0
              };
              if (pairing[player]?.game) {
                for (let key of Object.keys(pairing[player].game)) {
                  metricDict[key] = key;
                  body.gameData[player][key] = 0;
                }
              }
              playerNo++;
            }
          }

          Object.keys(metricDict).forEach((key) => {
            body.gameData.metrics.push(key);
          });

          try {
            setLoading(true);
            await postData(`/pairings/${pairing.id}/submitScores`, {
              body: body
            });

            if (!useWebSockets) setRefreshPairings(true);

            postNotification("success", "Pairing updated");
            setLoading(false);
            setPairing(null);
            setAnchorEl(null);
          } catch (err) {
            postNotification("error", err);
            setLoading(false);
          }
        }}
        disableRipple
      >
        {pairing?.pairingType?.toLowerCase() === "teampairing"
          ? "Give Both Teams Losses"
          : pairing?.pairingType?.toLowerCase() === "seatedpairing"
            ? "Give All Players Losses"
            : "Give Both/All Players Losses"}
      </MenuItem>

      <MenuItem
        onClick={async () => {
          try {
            await confirm({
              title: `Confirm Delete Pairing.`,
              description: "Are you sure you want to delete this pairing?",
              dialogProps: {
                sx: {
                  zIndex: 99999
                }
              }
            });
          } catch (err) {
            return;
          }
          try {
            setLoading(true);
            await deleteData(`/pairings/${pairing.id}`, {
              body: {
                pairingType: pairing?.pairingType || "Pairing"
              }
            });
            setRefreshPairings(true);
            postNotification("success", "Pairing deleted");
            setLoading(false);
            setPairing(null);
            setAnchorEl(null);
          } catch (err) {
            postNotification("error", err);
            setLoading(false);
          }
        }}
        disableRipple
      >
        {pairing?.pairingType?.toLowerCase() === "teampairing"
          ? "Delete Team Pairing"
          : "Delete Pairing"}
      </MenuItem>
    </StyledMenu>
  );
}

export function FiltersMenu(props) {
  const { anchorEl, setAnchorEl, event, filterType, setFilterType } = props;

  const open = Boolean(anchorEl);

  return (
    <>
      <StyledMenu
        id="filters-menu"
        MenuListProps={{
          "aria-labelledby": "filters-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          selected={filterType === "" ? true : false}
          onClick={async (e) => {
            setFilterType("");
            setAnchorEl(null);
          }}
          disableRipple
        >
          All Pairings
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          selected={filterType === "Submitted" ? true : false}
          onClick={async (e) => {
            setFilterType("Submitted");
            setAnchorEl(null);
          }}
          disableRipple
        >
          Submitted {event?.teamEvent && !event?.doublesEvent && "Team"}{" "}
          Pairings
        </MenuItem>

        <MenuItem
          selected={filterType === "Unsubmitted" ? true : false}
          onClick={async (e) => {
            setFilterType("Unsubmitted");
            setAnchorEl(null);
          }}
          disableRipple
        >
          Unsubmitted {event?.teamEvent && !event?.doublesEvent && "Team"}{" "}
          Pairings
        </MenuItem>

        {event?.teamEvent && !event?.doublesEvent && (
          <>
            <Divider
              sx={{
                mt: 2,
                mb: 2
              }}
            />
            <MenuItem
              selected={filterType === "Teams With Pairings" ? true : false}
              onClick={async (e) => {
                setFilterType("Teams With Pairings");
                setAnchorEl(null);
              }}
              disableRipple
            >
              Teams With Pairings
            </MenuItem>

            <MenuItem
              selected={filterType === "Teams Without Pairings" ? true : false}
              onClick={async (e) => {
                setFilterType("Teams Without Pairings");
                setAnchorEl(null);
              }}
              disableRipple
            >
              Teams Without Pairings
            </MenuItem>
          </>
        )}
      </StyledMenu>
    </>
  );
}
